import { useQueries, useQueryClient } from "@tanstack/react-query";
import { UserCheck } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DiscardGuard, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { FieldControl } from "@/features/contact-fields";
import { partiesOf } from "@/lib/accident";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { contactName } from "@/lib/contacts";
import { sameValues } from "@/lib/form";
import {
  useContractAgreements, useKeys, useLeadAccident, useLeadFormFields, useSendContract, useUpdateContact,
  useUpdateContactFields,
} from "@/lib/queries";
import type { Draft } from "@/lib/record-fields";
import {
  type AgreementValue,
  type ContractRefusal,
  agreementChanges,
  agreementGroups,
  agreementOf,
  agreementParty,
  agreementValues,
  answerToSave,
  changedHere,
  draftFor,
  choiceLines,
  chosenCheck,
  chosenOf,
  contractNotice,
  contractRefusal,
  contractToFix,
  offerOf,
  partiesSentMessage,
  refusedValue,
  savesOnPick,
  sentMessage,
  todayKey,
  untexted,
  valueProblem,
  versionOf,
} from "@/lib/send-contract";
import type { ContractCheck, ContractSent, LeadCard } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";

/**
 * Send contract on the record (ACL-418, ADR-134 in platform): the replacement for the `contract` tag
 * a rep adds in GoHighLevel.
 *
 * core-api builds the request from the record and asks the contract service while the rep waits, so
 * the dialog stays open on a refusal and says what to fix: the values to fill in on the record's
 * cards, or what an admin has to set up. What core-api already knows is said before the press
 * (ACL-419): a contact with no firm, or without a value every agreement needs, opens the dialog with
 * the reason beside a dimmed button (as `ConfirmDialog` does), so the rep is never left guessing.
 * The page asks the check again each time the dialog opens, so a value just filled in counts. The
 * dialog names the firm whose agreement goes out (ACL-420), so a rep sets the right firm first, and
 * a paused firm's is refused before the press with its reason.
 *
 * The values it lists to fill in, or a value the service refused (a firm's own required field, a date
 * in the future), are marked on the record when the rep closes the dialog (ACL-541, `onClosed`), each
 * where it stands with why under it, so they are filled in there. The name the agreement goes out
 * under is said for the rep to confirm with the lead before the press.
 *
 * The Phone page opens the same dialog from the call's card (ACL-560, `place="call"`): what it lists is
 * marked on the call's form instead, and a lead with no firm has the firm picked there too.
 *
 * The rep chooses the agreement (ACL-578, ADR-193 in platform), as the contract service's own manual
 * send lets an operator: every firm's agreements, the contact's own firm's first, with the one the
 * service would pick preselected. Another version (Spanish, minor) or another firm's replaces removing
 * tags by hand in GoHighLevel; another firm's becomes the contact's firm once it is sent, and the dialog
 * says so first. The chosen agreement's own values to fill in are what it lists and marks. Without the
 * list (a core-api or contract service before ACL-578) it sends as it did, from the check.
 *
 * The chosen agreement's values are shown, pre-filled from the record, for the rep to check and fix
 * without leaving the dialog (ACL-604), as the contract service's own manual send does: the contact's
 * details and every value core-api says the agreement is filled with (`needs`), required and optional.
 * Each is changed as the record changes it, by the same per-role rules: the details through the contact,
 * the rest as the form's answers; one this person may only read, or their form does not show, is shown
 * read-only, and blocks Send while it is required and empty. Send waits until every value is valid (none
 * required empty, no date in the future), saves what was changed, then presses.
 * On the call (`startDrafts`, `onSaved`) it starts from the answers the call's form has not saved yet,
 * and the form lets go of the ones it saved. Without `needs` (a core-api before ACL-604) it lists what
 * is missing, as before.
 *
 * It opens as the right-side panel our forms use (ACL-653), so the form behind stays in view. An answer
 * is saved when the rep leaves its field, or as it is picked, whether or not the contract goes out, so
 * the record and the call's form show it at once. The contact's own details wait for Send: the phone is
 * what the dialer calls. Leaving with a value changed asks first (Esc, the cross, a click outside);
 * Cancel does not, as on every other form.
 *
 * Everyone in the contact's accident is listed at the top with a checkbox each (ACL-671, ADR-226 in
 * platform): the contact ticked, the others unticked until the rep ticks them, and any of them, the
 * contact included, unticked again; a person whose own check says their agreement cannot go out is dimmed
 * with the reason and Open, and one who signed is done. Send sends the contact's agreement as before when
 * they are ticked, then one to each other person ticked, each through the same route with the agreement
 * core-api picks for them (a minor's with the guardian, ADR-134), texted to their own number, or to the
 * shared one in their own name. With the contact unticked their values below are not checked and nothing
 * is pressed for them. What went and what did not is said in one toast, and the call's accident panel
 * reads again.
 */
export function SendContractDialog({
  open, onOpenChange, contact, check, checking = false, onClosed, place = "record", startDrafts, onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
  check: ContractCheck | undefined;
  /** The check is being asked again: the button waits for its answer. */
  checking?: boolean;
  /** Told when the dialog closes: what it named to fix on the record (`contractToFix`), or null. */
  onClosed?: (found: ReturnType<typeof contractToFix>) => void;
  /** Where what it lists is filled in: the contact's record, or the call's form (ACL-560). */
  place?: "record" | "call";
  /** What the values start from where the record does not have them yet: the call form's unsaved
   *  answers (ACL-604). */
  startDrafts?: Readonly<Record<string, Draft>>;
  /** Told the form's answers it saved before the press, by key (ACL-604). */
  onSaved?: (keys: string[]) => void;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const send = useSendContract();
  const saveDetails = useUpdateContact();
  const saveAnswers = useUpdateContactFields();
  const agreements = useContractAgreements(contact.id, open);
  const form = useLeadFormFields(open ? contact.id : null);
  const [refusal, setRefusal] = React.useState<ContractRefusal | null>(null);
  // Sent, but its link was not texted (ACL-423): kept on screen with the link, and no second press.
  const [made, setMade] = React.useState<ReturnType<typeof untexted>>(null);
  // The agreement the rep picked, until the dialog opens again; null keeps the suggested one.
  const [picked, setPicked] = React.useState<number | null>(null);
  // The values typed here (ACL-604), by key, kept when another agreement is chosen; dropped on close.
  const [drafts, setDrafts] = React.useState<Record<string, Draft>>({});
  // Saving the values before the press, and why they were not.
  const [saving, setSaving] = React.useState(false);
  const [unsaved, setUnsaved] = React.useState<string | null>(null);
  // What each value the rep touched read before they did (ACL-653): leaving with one changed asks first.
  const [before, setBefore] = React.useState<Record<string, Draft>>({});
  // One answer saved at a time, so an older reply never overwrites a newer one in the form's cache.
  const answerSaves = React.useRef<Promise<void>>(Promise.resolve());
  // What was last sent for each answer, so leaving it again while that save is out does not repeat it.
  const answerSent = React.useRef<Record<string, Draft>>({});
  // Everyone else in the contact's accident (ACL-671), each with their own check, read while the dialog is
  // open. A record the reader may not open is not listed: there is nothing to send them.
  const keys = useKeys();
  const qc = useQueryClient();
  const accident = useLeadAccident(open ? contact.id : null);
  const others = React.useMemo(
    () => partiesOf(accident.data).filter((p) => p.may_open && p.id !== contact.id),
    [accident.data, contact.id],
  );
  const checks = useQueries({
    queries: others.map((p) => ({
      queryKey: keys.contactContract(p.id),
      queryFn: () => api.get<ContractCheck>(`/leads/${p.id}/contract`),
      enabled: open,
    })),
  });
  const parties = others.map((p, i) => agreementParty(p, checks[i]?.data, contact, formatPhoneNumber));
  // Whom the agreements go to: the contact, until the rep unticks them, and the others the rep ticks. One
  // ticked whose check then says no is not sent to: the box follows the check.
  const [own, setOwn] = React.useState(true);
  const [tickedIds, setTickedIds] = React.useState<Set<string>>(() => new Set());
  const ticked = parties.filter((p) => p.state === "ready" && tickedIds.has(p.id));
  const count = ticked.length + (own ? 1 : 0);
  // Read when it opens only: the call's form keeps typing while the dialog is up.
  const start = React.useRef(startDrafts);
  start.current = startDrafts;
  React.useEffect(() => {
    if (open) {
      setRefusal(null);
      setMade(null);
      setPicked(null);
      setDrafts({ ...(start.current ?? {}) });
      setUnsaved(null);
      setBefore({});
      setOwn(true);
      setTickedIds(new Set());
      answerSent.current = {};
    }
  }, [open]);
  const name = contactName(contact) ?? formatPhoneNumber(contact.phone);
  const offer = offerOf(agreements.data);
  const chosen = offer ? chosenOf(offer, picked) : null;
  // Every value the chosen agreement is filled with (ACL-604), once core-api says them and the form is read.
  const needs = chosen?.needs?.length ? chosen.needs : null;
  const values = needs && form.data ? agreementValues(needs, form.data.sections, user?.role) : null;
  const missing = new Set(chosen?.fields.map((f) => f.key));
  const today = todayKey();
  const problemOf = (value: AgreementValue) =>
    refusedValue(refusal, value.need.key) ?? valueProblem(value, drafts, missing, today);
  // What the dialog reads: the chosen agreement's own needs once there is a choice, else the check.
  // With its values shown, they say what is missing, each where it stands.
  const shown = offer ? (needs ? undefined : chosenCheck(chosen)) : check;
  // The contact's own values stand in the way only while their agreement is among the ones going out.
  const ownBlocked = needs
    ? values === null || values.some((v) => problemOf(v) !== null)
    : shown !== undefined && !shown.can_send;
  const blocked = own && ownBlocked;
  // The list is waited for, so a press never goes out before the rep could see what is chosen.
  const waiting = checking || agreements.isFetching || (needs !== null && values === null && form.isFetching);
  const setDraft = (key: string, draft: Draft) => {
    const value = values?.find((v) => v.need.key === key);
    setBefore((now) => (key in now ? now : { ...now, [key]: value ? draftFor(value, drafts) : "" }));
    setDrafts((now) => ({ ...now, [key]: draft }));
    setRefusal(null);
    setUnsaved(null);
  };
  /** Saves one answer the rep has finished with (ACL-653), if it is valid and changed. */
  const saveAnswer = (value: AgreementValue, now: Readonly<Record<string, Draft>>) => {
    const answers = answerToSave(value, now, missing, today);
    const key = value.need.key;
    if (!answers || sameValues(answerSent.current[key], now[key])) return;
    answerSent.current[key] = now[key]!;
    answerSaves.current = answerSaves.current.then(async () => {
      try {
        await saveAnswers.mutateAsync({ id: contact.id, values: answers });
        onSaved?.(Object.keys(answers));
      } catch (e) {
        delete answerSent.current[key];
        setUnsaved(e instanceof ApiError ? e.message : "Could not save the changes. Please try again.");
      }
    });
  };
  /** A value changed in the window: a pick is finished as it is made, a typed value when it is left. */
  const changeValue = (key: string, draft: Draft) => {
    setDraft(key, draft);
    const value = values?.find((v) => v.need.key === key);
    if (value?.field && savesOnPick(value.field)) saveAnswer(value, { ...drafts, [key]: draft });
  };
  const leaveValue = (value: AgreementValue) => {
    if (value.field && !savesOnPick(value.field) && value.need.key in before) saveAnswer(value, drafts);
  };
  /** Saves what was changed here (ACL-604): the contact's details, then the form's answers. */
  const save = async (): Promise<boolean> => {
    if (!values) return true;
    const { details, answers } = agreementChanges(values, drafts);
    setSaving(true);
    try {
      await answerSaves.current;
      if (Object.keys(details).length > 0) await saveDetails.mutateAsync({ id: contact.id, ...details });
      const keys = Object.keys(answers);
      if (keys.length > 0) {
        await saveAnswers.mutateAsync({ id: contact.id, values: answers });
        onSaved?.(keys);
      }
      return true;
    } catch (e) {
      setUnsaved(e instanceof ApiError ? e.message : "Could not save the changes. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };
  const submit = async () => {
    setRefusal(null);
    setUnsaved(null);
    // The contact's own first, with the agreement chosen, once their values are saved. A refusal of theirs
    // stops here, said in the window, before anyone else's goes out.
    let sent: ContractSent | null = null;
    if (own) {
      if (!(await save())) return;
      try {
        sent = await send.mutateAsync({ leadId: contact.id, agreementId: chosen?.id ?? null });
      } catch (e) {
        setRefusal(
          contractRefusal(e) ?? {
            code: "service_failed",
            detail: "Could not reach the server. Please try again.",
            fields: [],
          },
        );
        return;
      }
    }
    // The others ticked, one after another, each with the agreement core-api picks for them (ACL-671).
    // A refusal of someone else's is said in the toast, never thrown.
    const results: { name: string; sent: ContractSent | null; problem: string | null }[] = [];
    for (const party of ticked) {
      try {
        results.push({ name: party.name, sent: await send.mutateAsync({ leadId: party.id, agreementId: null }), problem: null });
      } catch (e) {
        const refused = contractRefusal(e);
        results.push({ name: party.name, sent: null, problem: refused?.detail ?? (e instanceof ApiError ? e.message : "could not be sent.") });
      }
    }
    if (results.length > 0) void qc.invalidateQueries({ queryKey: keys.contactAccident(contact.id) });
    const theirs = partiesSentMessage(results, sent !== null);
    const kept = sent ? untexted(sent) : null;
    if (kept) {
      setMade(kept);
      if (theirs) toast[theirs.tone](theirs.title, theirs.detail);
      return;
    }
    onClosed?.(null);
    onOpenChange(false);
    if (!sent) {
      if (theirs) toast[theirs.tone](theirs.title, theirs.detail);
      return;
    }
    const said = sentMessage(sent);
    if (theirs) toast[theirs.tone](theirs.title, `${said.detail} ${theirs.detail}`);
    else toast.success(said.title, said.detail);
  };
  /** However it is closed (Cancel, the cross, Escape, a click outside): what it listed is marked. With
   *  the values shown, each was said where it stands, so only a refusal naming none of them is. */
  const close = () => {
    onClosed?.(made ? null : contractToFix(shown, values ? refusalElsewhere(refusal, values) : refusal));
    onOpenChange(false);
  };
  // The name the agreement goes out under: as typed here once the values are shown, else the contact's.
  const typedName = (key: "first_name" | "last_name") => {
    const value = values?.find((v) => v.need.key === key);
    const draft = value?.field ? draftFor(value, drafts) : contact[key];
    return typeof draft === "string" ? draft.trim() : "";
  };
  const confirmName = values
    ? [typedName("first_name"), typedName("last_name")].filter(Boolean).join(" ") || null
    : contactName(contact);
  // What stands in the way of the contact's own, while it is among the ones going out.
  const notice = made || !own ? null : contractNotice(shown, values ? refusalElsewhere(refusal, values) : refusal);
  // The notice is part of what the dialog says (as ConfirmDialog puts its reason in the description),
  // so a screen reader hears why Send is dimmed when the dialog opens. Its region stays on the page
  // so a fresh answer is announced too; a danger callout is an alert already.
  const descriptionId = React.useId();
  const noticeId = React.useId();
  const confirmId = React.useId();
  const live = notice?.kind === "reason" && notice.tone === "danger" ? undefined : "polite";
  const where = place === "call" ? "the form" : "the record";
  // On the call a lead with no firm has it picked on the form (ACL-560); the record has Set firm. With
  // a choice, the agreement chosen names the firm.
  const firmOnForm = place === "call" && refusal === null && !offer && check?.reason === "no_firm";
  const pickerId = React.useId();
  const lines = offer && !made ? choiceLines(offer, chosen, contact.firm?.id ?? null) : [];
  // Only while it can be sent: beside a list to fill in, there is nothing yet to confirm.
  const asking = own && !made && !notice && !(values && blocked) && confirmName !== null;
  const fillId = React.useId();
  // Everyone in the accident, drawn over the agreement (ACL-671): in the agreement's own block, or by itself
  // where there is no list to choose from (a core-api before ACL-578).
  const whoGets =
    !made && parties.length > 0 ? (
      <fieldset className="rounded-md border border-border px-2.5 py-2" aria-label="Who gets an agreement">
        <legend className="px-1 text-2xs font-semibold uppercase tracking-wide text-text-subtle">
          Who gets an agreement
        </legend>
        <ul className="space-y-1.5 text-xs">
          <li className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={own}
              disabled={saving || send.isPending}
              onChange={(e) => setOwn(e.target.checked)}
              aria-label={name}
            />
            <span className="min-w-0">
              <span className="font-medium text-text">{name}</span>
              <span className="text-text-muted"> · texted to {formatPhoneNumber(contact.phone)}</span>
            </span>
          </li>
          {parties.map((party) => (
            <li key={party.id} className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={party.state === "ready" && tickedIds.has(party.id)}
                disabled={party.state !== "ready" || saving || send.isPending}
                onChange={(e) => {
                  const on = e.target.checked;
                  setTickedIds((now) => {
                    const next = new Set(now);
                    if (on) next.add(party.id);
                    else next.delete(party.id);
                    return next;
                  });
                }}
                aria-label={party.name}
              />
              <span className="min-w-0">
                <span className="font-medium text-text">{party.name}</span>
                <span className="text-text-muted">
                  {" · "}
                  {party.state === "ready" ? party.texted : party.state === "checking" ? "checking their record…" : party.reason}
                </span>
                {party.state === "fix" && (
                  <>
                    {" "}
                    <Link to={`/contacts/${party.id}`} className="font-medium text-accent-11 hover:underline">
                      Open
                    </Link>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-2xs text-text-muted">
          {count === 0
            ? "Nobody is ticked: tick who gets an agreement."
            : count === 1
              ? "One agreement goes out."
              : `${count} agreements go out, one link each. Everyone signs their own.`}
          {" "}One that cannot be sent is said here.
        </p>
      </fieldset>
    ) : null;
  // Why Send is dimmed with the values shown, said once beside it for a screen reader too.
  const fillSaid = values && blocked && !made && !notice ? "Fill in or fix the marked values to send it." : null;
  const describedBy = [descriptionId, asking && confirmId, notice && noticeId, fillSaid && fillId]
    .filter(Boolean)
    .join(" ");
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      leaveAsk={{
        title: "Are you sure you want to leave?",
        description:
          "The contract has not been sent. Values already saved stay on the contact. A changed name, phone or e-mail, and anything marked as wrong, is lost.",
        confirmLabel: "Leave",
      }}
    >
      <DialogContent variant="drawer" size="md" aria-describedby={describedBy}>
        <DiscardGuard changed={!made && changedHere(before, drafts)} />
        <DialogHeader>
          <DialogTitle>Send {name} their contract?</DialogTitle>
          <DialogDescription id={descriptionId}>
            {needs ? (
              <>
                Check the values the agreement is filled in with. An answer is saved to the contact when you leave
                its field, a name, phone or e-mail when you send, and the signing link is texted to the phone below.
              </>
            ) : (
              <>
                {offer ? "The agreement" : agreementOf(check)} is filled in from this record, and the signing link
                is texted to {formatPhoneNumber(contact.phone)}.
              </>
            )}
          </DialogDescription>
          {/* The agreement goes out under this name, so the rep reads it back to the lead first (ACL-541). */}
          {asking && (
            <p id={confirmId} className="flex items-center gap-1.5 pt-1 text-sm font-medium text-warning-11">
              <UserCheck className="size-4 shrink-0" aria-hidden />
              <span>
                Please confirm name: <span className="text-text">{confirmName}</span>
              </span>
            </p>
          )}
        </DialogHeader>
        {!offer && whoGets && <DialogBody className="pb-0">{whoGets}</DialogBody>}
        {offer && !made && (
          <DialogBody className={notice ? "pb-0" : undefined}>
            {whoGets && <div className="mb-4">{whoGets}</div>}
            <Field
              label="Agreement"
              htmlFor={pickerId}
              hint={
                lines.length > 0 ? lines.map((line) => <span key={line} className="block">{line}</span>) : undefined
              }
            >
              <Select
                value={chosen ? String(chosen.id) : ""}
                onValueChange={(next) => {
                  setPicked(Number(next));
                  setRefusal(null);
                }}
              >
                <SelectTrigger id={pickerId} aria-label="Agreement">
                  <SelectValue placeholder="Choose the agreement…" />
                </SelectTrigger>
                <SelectContent>
                  {agreementGroups(offer.agreements).map((group, i) => (
                    <React.Fragment key={group.firmId}>
                      {i > 0 && <SelectSeparator />}
                      <SelectGroup>
                        <SelectLabel>{group.firm}</SelectLabel>
                        {group.agreements.map((a) => (
                          <SelectItem key={a.id} value={String(a.id)} description={versionOf(a)}>
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {needs && (
              <AgreementValues
                contact={contact}
                values={values}
                loading={form.isLoading}
                drafts={drafts}
                problemOf={problemOf}
                onChange={changeValue}
                onLeave={leaveValue}
              />
            )}
          </DialogBody>
        )}
        <div
          id={noticeId}
          aria-live={live}
          className={cn("flex min-h-0 flex-col", (!offer || made) && "flex-1")}
        >
          {made && (
            <DialogBody>
              <Callout tone="warning">
                <p>The agreement is ready, but its link did not go out by text: {made.problem}</p>
                {made.emailedTo && <p className="mt-1">DocuSeal did e-mail it to {made.emailedTo}.</p>}
                <p className="mt-1 break-all font-mono text-xs">{made.link}</p>
              </Callout>
            </DialogBody>
          )}
          {unsaved && (
            <DialogBody>
              <Callout tone="danger">Nothing was sent: the changes could not be saved. {unsaved}</Callout>
            </DialogBody>
          )}
          {notice && (
            <DialogBody>
              {notice.kind === "fill" ? (
                <Callout tone="warning">
                  <p>
                    {notice.pressed
                      ? `Fill these in on ${where} first, then send again:`
                      : `Fill these in on ${where} first:`}
                  </p>
                  <ul className="mt-1 list-disc pl-5">
                    {notice.fields.map((f) => <li key={f.key}>{f.label}</li>)}
                  </ul>
                  <p className="mt-2">They are highlighted on {where} when you close this.</p>
                </Callout>
              ) : (
                <Callout tone={notice.tone}>
                  {notice.detail}
                  {firmOnForm && <p className="mt-2">You can choose it on the form when you close this.</p>}
                </Callout>
              )}
            </DialogBody>
          )}
        </div>
        <DialogFooter>
          {made ? (
            <Button onClick={close}>Close</Button>
          ) : (
            <>
              {fillSaid && (
                <p id={fillId} className="mr-auto self-center text-xs text-text-muted">{fillSaid}</p>
              )}
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button loading={saving || send.isPending} disabled={blocked || waiting || count === 0} onClick={() => void submit()}>
                {count > 1 ? `Send ${count} contracts` : "Send contract"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A refusal after the press that names none of the values shown (ACL-604), said in the notice: the
 *  ones it names are said under them. */
function refusalElsewhere(refusal: ContractRefusal | null, values: readonly AgreementValue[]): ContractRefusal | null {
  if (!refusal) return null;
  const shown = new Set(values.map((v) => v.need.key));
  return refusal.fields.length > 0 && refusal.fields.every((f) => shown.has(f.key)) ? null : refusal;
}

/**
 * The chosen agreement's values (ACL-604), each pre-filled and changed as the record changes it, with
 * what is wrong with it under it. One this person cannot change here is drawn read-only: a field their
 * role may only read says so, and one their form does not show is named, with the contact's own detail
 * as the contact card has it (the name, the phone, the e-mail), else no value.
 */
function AgreementValues({
  contact, values, loading, drafts, problemOf, onChange, onLeave,
}: {
  contact: LeadCard;
  values: AgreementValue[] | null;
  loading: boolean;
  drafts: Readonly<Record<string, Draft>>;
  problemOf: (value: AgreementValue) => string | null;
  onChange: (key: string, draft: Draft) => void;
  /** Focus left the value: a typed one is finished (ACL-653). */
  onLeave: (value: AgreementValue) => void;
}) {
  if (values === null) {
    return (
      <p className="mt-4 text-sm text-text-muted">
        {loading ? "Reading the values from the record…" : "The record's values could not be read. Close this and try again."}
      </p>
    );
  }
  return (
    <div role="group" aria-label="The agreement's values" className="mt-4 grid gap-3 sm:grid-cols-2">
      {values.map((value) => {
        const { need, field, how } = value;
        const problem = problemOf(value) ?? undefined;
        if (field === null) {
          const id = `send-contract-${need.key}`;
          const known = cardDetail(contact, need.key);
          return (
            <Field
              key={need.key}
              label={need.label}
              htmlFor={id}
              required={need.required}
              error={problem}
              hint={known ? "Changed on the record." : "Not shown on your form."}
            >
              <Input disabled value={known ?? ""} invalid={Boolean(problem)} placeholder="Not shown to you" />
            </Field>
          );
        }
        // Drawn with the agreement's label and its required mark; a detail the role may change is
        // changed here as the details panel changes it.
        const drawn = {
          ...field,
          title: need.label,
          required: need.required,
          ...(how === "detail" ? { can_edit: true, storage: "value" as const } : {}),
          ...(how === "read" ? { can_edit: false } : {}),
        };
        return (
          <div key={need.key} className="contents" onBlur={() => onLeave(value)}>
            <FieldControl
              entity="lead"
              field={drawn}
              draft={draftFor(value, drafts)}
              problem={problem}
              idPrefix="send-contract"
              onChange={(draft) => onChange(need.key, draft)}
              onPick={(choice) => onChange(need.key, choice ? String(choice.id) : "")}
            />
          </div>
        );
      })}
    </div>
  );
}

/** One of the contact's own details as the contact card holds it, for a value the form does not show. */
function cardDetail(contact: LeadCard, key: string): string | null {
  if (key === "phone") return formatPhoneNumber(contact.phone);
  if (key === "first_name" || key === "last_name" || key === "email") return contact[key] ?? null;
  return null;
}
